import React from 'react';
import LastTwelveWeeks from '../common/components/LastTwelveWeeks';
import BalancedFeedback from '../common/components/BalancedFeedback';
import CoachingHistory from '../common/components/CoachingHistory';
import NewAction from '../common/components/NewAction';
import FinalSignatures from '../common/components/FinalSignatures';
import { ActivitySession } from '../PeopleActivityScreen';

interface PulseCheckContentProps {
  session: ActivitySession;
  /** All state + handlers come from usePulseCheck */
  metricRows: any[];
  periodMetricRows: any[];
  feedback: any;
  openItems: any[];
  historyItems: any[];
  actionDraft: any;
  sigData: any;
  setFeedback: (v: any) => void;
  setActionDraft: (v: any) => void;
  setSigData: (v: any) => void;
  onSignatureUploadingChange: (isUploading: boolean) => void;
  onFollowUpResult: (id: string, result: any) => void;
  onFollowUpFeedback: (id: string, text: string) => void;
}

/**
 * PulseCheckContent
 *
 * Pure content fragment — renders the Pulse Check sections only.
 * No root View, no ScrollView, no footer.
 * Intended to be rendered as children of PeopleActivityScreen.
 */
const PulseCheckContent: React.FC<PulseCheckContentProps> = ({
  session,
  metricRows,
  periodMetricRows,
  feedback,
  openItems,
  historyItems,
  actionDraft,
  sigData,
  setFeedback,
  setActionDraft,
  setSigData,
  onSignatureUploadingChange,
  onFollowUpResult,
  onFollowUpFeedback,
}) => {
  const isReadOnly = session.isReadOnly === true;
  const signers = [
    { role: 'Employee', expectedName: session.employeeName },
    { role: 'Manager',  expectedName: session.managerName },
  ];

  return (
    <>
      {/* 1. 12-Week Rolling Metrics */}
      <LastTwelveWeeks
        title="12-Week Rolling Metrics"
        rows={metricRows}
        periodRows={periodMetricRows}
        periodTitle="12-Period Rolling Metrics"
        activityType="pulse_check"
      />

      {/* 2. Balanced Feedback */}
      <BalancedFeedback
        feedback={feedback}
        onChange={setFeedback}
        isReadOnly={isReadOnly}
      />

      {/* 3. Coaching History */}
      <CoachingHistory
        openItems={openItems}
        historyItems={historyItems}
        activityType="pulse_check"
        isReadOnly={isReadOnly}
        onFollowUpResultChange={onFollowUpResult}
        onFollowUpFeedbackChange={onFollowUpFeedback}
      />

      {/* 4. New Action — hidden for completed activities (nothing can be added anymore) */}
      {!isReadOnly && (
        <NewAction
          draft={actionDraft}
          onDraftChange={setActionDraft}
          activityType="pulse_check"
          isGated={openItems.length > 0 && !openItems.every((i: any) => i.followUpResult === 'Complete')}
          noOpenItem={openItems.length === 0}
          gateMessage={
            openItems.length > 0 && !openItems.every((i: any) => i.followUpResult === 'Complete')
              ? 'Select Shows Improvement, No Improvement Shown, or Complete on every open item before adding a new action.'
              : undefined
          }
        />
      )}

      {/* 5. Final Signatures */}
      <FinalSignatures
        signatureData={sigData}
        onSignatureChange={setSigData}
        signers={signers}
        activityType="pulse_check"
        isReadOnly={isReadOnly}
        onUploadingChange={onSignatureUploadingChange}
      />
    </>
  );
};

export default PulseCheckContent;
